import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ListGroup, Badge, Alert, Spinner, Modal } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { TIER_PERMISSIONS } from '../shared/tierPermissions';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import { CONSTANTS } from '../shared/constants';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter, { OPEN_CONTACT_US_MODAL_EVENT } from '../components/VersionFooter';
import { getCachedAccountData, refreshAccountData } from '../shared/accountCache';

export default function Pricing() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentTier, setCurrentTier] = useState('free');
  const [billingInterval, setBillingInterval] = useState('year'); // 'month' or 'year'
  const [processingTier, setProcessingTier] = useState(null); // 'pro' or 'proplus' or null
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchUserTier = async () => {
      if (currentUser) {
        try {
          const accountData = await getCachedAccountData();
          setCurrentTier(accountData?.subscription?.tier || 'free');
        } catch (error) {
          console.error('Error fetching account data:', error);
        }
      }
    };
    
    fetchUserTier();
    
    // Check for payment status in URL
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      // Refresh account data to get updated tier
      const refreshData = async () => {
        try {
          const accountData = await refreshAccountData();
          setCurrentTier(accountData?.subscription?.tier || 'free');
          setSuccess(t('pricing.paymentSuccessfulMessage'));
          // Remove payment param from URL
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('payment');
          navigate(`/pricing?${newSearchParams.toString()}`, { replace: true });
        } catch (error) {
          console.error('Error refreshing account data:', error);
          setSuccess(t('pricing.paymentSuccessfulMessage'));
        }
      };
      refreshData();
    } else if (paymentStatus === 'cancelled') {
      setError(t('pricing.paymentCancelled'));
      // Remove payment param from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('payment');
      navigate(`/pricing?${newSearchParams.toString()}`, { replace: true });
    }
  }, [currentUser, searchParams, navigate, t]);

  // Reset processing state when page becomes visible (user navigated back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setProcessingTier(null);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleUpgrade = async (targetTier = 'pro') => {
    if (!currentUser) {
      setError(t('pricing.pleaseLoginToUpgrade'));
      return;
    }

    setProcessingTier(targetTier);
    setError('');

    try {
      // Lemon Squeezy checkout
      let variantId;
      if (targetTier === 'proplus') {
        variantId = billingInterval === 'year' 
          ? process.env.REACT_APP_LEMONSQUEEZY_VARIANT_ID_PROPLUS_YEARLY 
          : process.env.REACT_APP_LEMONSQUEEZY_VARIANT_ID_PROPLUS_MONTHLY;
      } else {
        variantId = billingInterval === 'year' 
          ? process.env.REACT_APP_LEMONSQUEEZY_VARIANT_ID_YEARLY 
          : process.env.REACT_APP_LEMONSQUEEZY_VARIANT_ID_MONTHLY;
      }

      console.log('[Pricing] Using Lemon Squeezy with:', {
        targetTier,
        variantId,
        billingInterval
      });

      if (!variantId) {
        const interval = billingInterval === 'year' ? t('pricing.yearly') : t('pricing.monthly');
        setError(t('pricing.paymentConfigError', { tier: targetTier, interval }));
        setProcessingTier(null);
        return;
      }

      const response = await apiCall(
        `/api/lemonsqueezy?action=${ApiActions.CREATE_CHECKOUT_SESSION}`,
        { variantId, billingInterval, targetTier },
        'POST'
      );

      if (response.success && response.url) {
        // Redirect to checkout page
        window.location.href = response.url;
      } else {
        setError(response.message || t('pricing.failedToCreateCheckout'));
        setProcessingTier(null);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setError(t('pricing.errorOccurred'));
      setProcessingTier(null);
    }
  };

  // Calculate dollar savings for yearly plans (Pro only; Pro+ is contact-us)
  const proYearlySavings = (parseFloat(CONSTANTS.PRICES.PRO_MONTHLY.replace('$', '')) * 12) - parseFloat(CONSTANTS.PRICES.PRO_YEARLY.replace('$', ''));

  return (
    <>
      <SlidingMenu title={t('pricing.title')} />
      
      <Container className="py-5">
        
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold mb-3">{t('pricing.pageTitle')}</h1>
          <p className="lead text-muted">{t('pricing.subtitle')}</p>
        </div>

        {/* Billing Toggle */}
        <div className="text-center mb-5">
          <div className="btn-group" role="group">
            <Button
              variant={billingInterval === 'month' ? 'primary' : 'outline-primary'}
              onClick={() => setBillingInterval('month')}
            >
              {t('pricing.monthly')}
            </Button>
            <Button
              variant={billingInterval === 'year' ? 'primary' : 'outline-primary'}
              onClick={() => setBillingInterval('year')}
            >
              {t('pricing.yearly')}
              <Badge bg="success" className="ms-2">{t('pricing.payFor10Months')}</Badge>
            </Button>
          </div>
        </div>

        {/* Pro free for 3 months – only for free/pro, not admin or proplus */}
        {currentTier !== 'admin' && currentTier !== 'proplus' && (
          <Alert variant="info" className="text-center mb-5" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <p className="mb-0">{t('pricing.specialPricingMessage')}</p>
          </Alert>
        )}

        <Row className="justify-content-center g-4">
          {/* Free Tier */}
          <Col md={6} lg={4}>
            <Card className={`h-100 shadow ${currentTier === 'free' ? 'border-primary border-3' : ''}`}>
              {currentTier === 'free' && (
                <div className="position-absolute top-0 start-50 translate-middle">
                  <Badge bg="primary" className="px-3 py-2">{t('pricing.currentPlan')}</Badge>
                </div>
              )}
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h3 className="fw-bold">{TIER_PERMISSIONS.free.displayName}</h3>
                  <div className="display-4 fw-bold my-3">
                    $0
                    <span className="fs-5 text-muted">{t('pricing.forever')}</span>
                  </div>
                  <p className="text-muted">{t('pricing.perfectForTrying')}</p>
                </div>

                <ListGroup variant="flush" className="mb-4">
                  {TIER_PERMISSIONS.free.features.map((feature, idx) => (
                    <ListGroup.Item key={idx} className="border-0 px-0">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      {typeof feature === 'string' ? feature : t(feature.key, feature.params || {})}
                    </ListGroup.Item>
                  ))}
                </ListGroup>

                <div className="d-grid">
                  {currentTier === 'free' ? (
                    <Button variant="outline-primary" disabled>
                      {t('pricing.currentPlan')}
                    </Button>
                  ) : (
                    <Button variant="outline-primary" onClick={() => navigate('/plans')}>
                      {t('pricing.goToTrips')}
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Pro Tier */}
          <Col md={6} lg={4}>
            <Card className={`h-100 shadow-lg border-success border-2 ${currentTier === 'pro' ? 'border-3' : ''}`}>
              <div className="position-absolute top-0 start-50 translate-middle">
                <Badge bg="success" className="px-3 py-2">
                  {currentTier === 'pro' ? t('pricing.currentPlan') : t('pricing.mostPopular')}
                </Badge>
              </div>
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h3 className="fw-bold text-success">{t('pricing.tier.pro')}</h3>
                  <div className="display-4 fw-bold my-3">
                    {billingInterval === 'year' ? 
                      CONSTANTS.PRICES.PRO_YEARLY_MONTHLY_EQUIVALENT : 
                      CONSTANTS.PRICES.PRO_MONTHLY}
                    <span className="fs-5 text-muted">{t('pricing.perMonth')}</span>
                  </div>
                  {billingInterval === 'year' && (
                    <p className="text-success">
                      {CONSTANTS.PRICES.PRO_YEARLY}{t('pricing.perYear')} ({t('pricing.save')} ${proYearlySavings})
                    </p>
                  )}
                  <p className="text-muted">{t('pricing.forSeriousTravelers')}</p>
                </div>

                <ListGroup variant="flush" className="mb-4">
                  {TIER_PERMISSIONS.pro.features.map((feature, idx) => (
                    <ListGroup.Item key={idx} className="border-0 px-0">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      {typeof feature === 'string' ? feature : t(feature.key, feature.params || {})}
                    </ListGroup.Item>
                  ))}
                </ListGroup>

                <div className="d-grid">
                  {currentTier === 'pro' || currentTier === 'admin' ? (
                    <Button variant="success" disabled>
                      {t('pricing.currentPlan')}
                    </Button>
                  ) : (
                    <Button 
                      variant="success" 
                      size="lg" 
                      onClick={() => handleUpgrade('pro')}
                      disabled={processingTier || !currentUser}
                    >
                      {processingTier === 'pro' ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          {t('pricing.processing')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-rocket-takeoff me-2"></i>
                          {t('pricing.upgradeToPro')}
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {!currentUser && currentTier === 'free' && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    <small>
                      <i className="bi bi-info-circle me-2"></i>
                      {t('pricing.pleaseLoginToUpgrade')}
                    </small>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Pro+ Tier - contact us only; no direct purchase */}
          <Col md={6} lg={4}>
            <Card className={`h-100 shadow ${currentTier === 'proplus' ? 'border-warning border-3' : 'border-warning'}`}>
              <div className="position-absolute top-0 start-50 translate-middle">
                <Badge bg="warning" text="dark" className="px-3 py-2">
                  {currentTier === 'proplus' ? t('pricing.currentPlan') : t('pricing.forOrganizers')}
                </Badge>
              </div>
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h3 className="fw-bold text-warning">{t('pricing.tier.proplus')}</h3>
                  <div className="display-6 fw-bold my-3 text-warning">
                    {t('pricing.proplusContactUs')}
                  </div>
                  <p className="text-muted">{t('pricing.forTourGuides')}</p>
                </div>

                <ListGroup variant="flush" className="mb-4">
                  {TIER_PERMISSIONS.proplus.features.map((feature, idx) => (
                    <ListGroup.Item key={idx} className="border-0 px-0">
                      <i className="bi bi-check-circle-fill text-warning me-2"></i>
                      {typeof feature === 'string' ? feature : t(feature.key, feature.params || {})}
                    </ListGroup.Item>
                  ))}
                </ListGroup>

                <div className="d-grid">
                  {currentTier === 'proplus' || currentTier === 'admin' ? (
                    <Button variant="warning" disabled>
                      {t('pricing.currentPlan')}
                    </Button>
                  ) : (
                    <Button 
                      variant="outline-warning" 
                      size="lg" 
                      onClick={() => window.dispatchEvent(new CustomEvent(OPEN_CONTACT_US_MODAL_EVENT))}
                    >
                      <i className="bi bi-envelope me-2"></i>
                      {t('pricing.proplusContactUs')}
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* FAQ Section */}
        <div className="mt-5 pt-5">
          <h2 className="text-center mb-4">{t('pricing.faq')}</h2>
          <Row className="justify-content-center">
            <Col md={8}>
              <div className="mb-4">
                <h5>{t('pricing.faq1Question')}</h5>
                <p className="text-muted">
                  {t('pricing.faq1Answer')}
                </p>
              </div>
              <div className="mb-4">
                <h5>{t('pricing.faq2Question')}</h5>
                <p className="text-muted">
                  {t('pricing.faq2Answer')}
                </p>
              </div>
              <div className="mb-4">
                <h5>{t('pricing.faq3Question')}</h5>
                <p className="text-muted">
                  {t('pricing.faq3Answer')}
                </p>
              </div>
              <div className="mb-4">
                <h5>{t('pricing.faq4Question')}</h5>
                <p className="text-muted">
                  {t('pricing.faq4Answer')}
                </p>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Success Modal - Always Visible, High Z-Index */}
      <Modal 
        show={!!success} 
        onHide={() => setSuccess('')}
        centered
        backdrop="static"
        style={{ zIndex: 10000 }}
      >
        <Modal.Header closeButton />
        <Modal.Body className="text-center py-4">
          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
          <h4 className="mt-3 mb-2">{t('pricing.paymentSuccessful')}</h4>
          <p className="mb-0 fs-5">{success}</p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="primary" onClick={() => setSuccess('')}>
            {t('pricing.gotIt')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Modal - Always Visible, High Z-Index */}
      <Modal 
        show={!!error} 
        onHide={() => setError('')}
        centered
        backdrop="static"
        style={{ zIndex: 10000 }}
      >
        <Modal.Header closeButton />
        <Modal.Body className="text-center py-4">
          <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3 mb-0 fs-5">{error}</p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="primary" onClick={() => setError('')}>
            {t('common.ok')}
          </Button>
        </Modal.Footer>
      </Modal>

      <VersionFooter />
    </>
  );
}

